/**
 * Role functions are used for accessing role data.
 * @module Role
 * @since 0.0.6
 */
import GlobalFunctions from '../global'

module.exports = {
	/**
	 * Call to unoapp API that returns a list of roles in the business.
	 * @function
	 * @param {integer} businessId - The business that we want to get roles for.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	getRoles (businessId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/role/list',
				data: {business_id: businessId},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that returns a list of roles in the location.
	 * @function
	 * @param {integer} locationId - The business that we want to get roles for.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	getLocationRoles (locationId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/location_role/list',
				data: {location_id: locationId},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that returns an array of role details for the specified business.
	 * @function
	 * @param {integer} businessId - The id of the business.
	 * @param {integer} pageNumber - The current page that we are retrieving results for.
	 * @param {integer} recordsPerPage - The number of records to return per page.
	 * @param {object} searchCriteria - Object with all criteria to be searched by.
	 * @param {object} sortCriteria - The field and order to be sorted by.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	getRolePages (businessId, pageNumber, recordsPerPage, searchCriteria, sortCriteria) {
		let httpParams = {business_id: businessId, page_number: pageNumber, records_per_page: recordsPerPage}
		if (sortCriteria) {
			for (let sortKey in sortCriteria) {
				httpParams[sortKey] = sortCriteria[sortKey]
			}
		}

		if (searchCriteria) {
			for (let searchKey in searchCriteria) {
				httpParams[searchKey] = searchCriteria[searchKey]
			}
		}
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/role/search',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that returns an array of role details for the specified location.
	 * @function
	 * @param {integer} businessId - The id of the business.
	 * @param {integer} locationId - The id of the location.
	 * @param {integer} pageNumber - The current page that we are retrieving results for.
	 * @param {integer} recordsPerPage - The number of records to return per page.
	 * @param {object} searchCriteria - Object with all criteria to be searched by.
	 * @param {object} sortCriteria - The field and order to be sorted by.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	getLocationRolePages (businessId, locationId, pageNumber, recordsPerPage, searchCriteria, sortCriteria) {
		let httpParams = {business_id: businessId, location_id: locationId, page_number: pageNumber, records_per_page: recordsPerPage}
		if (sortCriteria) {
			for (let sortKey in sortCriteria) {
				httpParams[sortKey] = sortCriteria[sortKey]
			}
		}

		if (searchCriteria) {
			for (let searchKey in searchCriteria) {
				httpParams[searchKey] = searchCriteria[searchKey]
			}
		}
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/location_role/search',
				data: httpParams,
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that creates a new role.
	 * @function
	 * @param {integer} businessId - The business to create the role under.
	 * @param {object} role - The role to be created.
	 * @param {boolean} bypassConflict - Whether or not we should bypass any conflicts.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	createRole (businessId, role, bypassConflict) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PUT',
				dataType: 'json',
				url: '/role',
				data: {business_id: businessId, role_name: role.name, approval_permission: GlobalFunctions.convertBooleanToInt(role.approval_permission), app_permissions: role.app_permissions, bypass_conflict: GlobalFunctions.convertBooleanToInt(bypassConflict)},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that retrieves an existing role.
	 * @function
	 * @param {object} roleId - The id of the role to be retrieved.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	getRole (roleId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/role/' + roleId,
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that retrieves an existing location role.
	 * @function
	 * @param {object} roleId - The id of the location role to be retrieved.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.9
	 */
	getLocationRole (roleId) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'POST',
				dataType: 'json',
				url: '/location_role/' + roleId,
				data: {},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that modifies an existing role.
	 * @function
	 * @param {object} role - The role to be modified.
	 * @param {boolean} bypassConflict - Whether or not we should bypass any conflicts.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	modifyRole (role, bypassConflict) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'PATCH',
				dataType: 'json',
				url: '/role/' + role.role_id,
				data: {role_name: role.role_name, approval_permission: GlobalFunctions.convertBooleanToInt(role.approval_permission), app_permissions: role.app_permissions, bypass_conflict: GlobalFunctions.convertBooleanToInt(bypassConflict)},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	},
	/**
	 * Call to unoapp API that deletes the specified role.
	 * @function
	 * @param {object} roleId - The role id of the role that is being deleted.
	 * @param {boolean} bypassConflict - Whether or not we should bypass any conflicts.
	 * @returns {object} A promise that will return either a success object or an error object.
	 * @version 0.0.6
	 */
	deleteRole (roleId, bypassConflict) {
		return new Promise((resolve, reject) => {
			GlobalFunctions.$ajax({
				method: 'DELETE',
				dataType: 'json',
				url: '/role/' + roleId,
				data: {bypass_conflict: GlobalFunctions.convertBooleanToInt(bypassConflict)},
				success (response) {
					resolve(response)
				},
				error (e) {
					let _this = this
					let _wrapper = GlobalFunctions.refreshWrapper((e, status) => {
						if (status === 'error') {
							reject(e)
						} else {
							GlobalFunctions.$ajax(_this)
						}
					})
					_wrapper(e)
				}
			})
		})
	}
}



// WEBPACK FOOTER //
// ./src/controllers/Roles.js